import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Table, Button, message, Input, Row, Col, Space,
  Card, Typography, Divider, Tag, Tooltip, Avatar,
  Badge, Select, Form, Drawer, Modal
} from "antd";
import {
  SearchOutlined, DownloadOutlined, EditOutlined, DeleteOutlined,
  UserOutlined, FileTextOutlined, CrownOutlined, UserSwitchOutlined,
  ArrowUpOutlined, ArrowDownOutlined, FilterOutlined, BankOutlined,
  PhoneOutlined, EnvironmentOutlined, PlusOutlined, MinusOutlined,
  ClearOutlined, ReloadOutlined, RightOutlined, DownOutlined,
  LockOutlined, MailOutlined
} from "@ant-design/icons";
import axios from "axios";
import AppAdminLayout from "./components/AdminLayout";
import { LanguageContext } from "./LanguageContext";
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const UnifiedUserCustomerManagement = () => {
  const [users, setUsers] = useState([]);
  const [allUserDetails, setAllUserDetails] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [userForm] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef(null);

  const { language, setLanguage, translations } = useContext(LanguageContext);
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  // ── Veri çekme ────────────────────────────────────────────
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const usersResponse = await axios.get("https://json-configrator-api.root.myoctoopidev.com/User/GetAll");
      const userData = Array.isArray(usersResponse.data) ? usersResponse.data : usersResponse.data.data;
      const detailsResponse = await axios.get("https://json-configrator-api.root.myoctoopidev.com/Customer/GetAllUserDetail");
      if (detailsResponse.data.success) {
        const userDetailsMap = new Map();
        detailsResponse.data.data.forEach(d => userDetailsMap.set(d.userId, d.customers));
        const enriched = userData.map(u => ({
          ...u,
          customers: userDetailsMap.get(u.id) || [],
          configCount: (userDetailsMap.get(u.id) || []).length
        }));
        setUsers(userData);
        setAllUserDetails(enriched);
        setFilteredUsers(enriched);
      }
    } catch {
      message.error("Veri alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAllData(); }, []);

  // ── Filtreler ─────────────────────────────────────────────
  const [filters, setFilters] = useState({
    name: '', surname: '', email: '', companyName: '',
    customerPhone: '', customerCountry: '', caravanType: '',
    role: null, configName: '', configId: '', hasConfigs: null
  });

  const applyFilters = () => {
    setFilteredUsers(allUserDetails.filter(user => {
      const ok = (key, val) => !val || user[key]?.toLowerCase().includes(val.toLowerCase());
      const roleMatch = filters.role === null || user.role === filters.role;
      const hasConfigsMatch = filters.hasConfigs === null ||
        (filters.hasConfigs === true && user.customers.length > 0) ||
        (filters.hasConfigs === false && user.customers.length === 0);
      let configMatch = true;
      if (filters.configName || filters.configId) {
        configMatch = user.customers.some(c =>
          (!filters.configName || (c.configName?.toLowerCase().includes(filters.configName.toLowerCase()) || c.id.toLowerCase().includes(filters.configName.toLowerCase()))) &&
          (!filters.configId || c.id.toLowerCase().includes(filters.configId.toLowerCase()))
        );
        if (user.customers.length === 0 && (filters.configName || filters.configId)) configMatch = false;
      }
      return ok('name', filters.name) && ok('surname', filters.surname) &&
             ok('email', filters.email) && ok('companyName', filters.companyName) &&
             ok('customerCountry', filters.customerCountry) && ok('caravanType', filters.caravanType) &&
             (!filters.customerPhone || user.customerPhone?.includes(filters.customerPhone)) &&
             roleMatch && hasConfigsMatch && configMatch;
    }));
  };

  useEffect(() => { applyFilters(); }, [filters, allUserDetails]);

  const clearAllFilters = () => {
    setFilters({ name: '', surname: '', email: '', companyName: '', customerPhone: '',
      customerCountry: '', caravanType: '', role: null, configName: '', configId: '', hasConfigs: null });
    setQuickSearch('');
  };

  // ── Aksiyon handler'ları ──────────────────────────────────
  const handleRoleChange = async (id, up) => {
    if (up && !window.confirm("Bu kullanıcıyı Admin yapmak istediğinizden emin misiniz?")) return;
    try {
      await axios.put(up
        ? "https://json-configrator-api.root.myoctoopidev.com/User/UserUpRole"
        : "https://json-configrator-api.root.myoctoopidev.com/User/UserDownRole", { id });
      message.success(`Rol başarıyla ${up ? "yükseltildi" : "düşürüldü"}.`);
      fetchAllData();
    } catch { message.error("İşlem başarısız."); }
  };

  const handleResetPassword = async (id, name, surname) => {
    if (!window.confirm(`"${name} ${surname}" kullanıcısının şifresini sıfırlamak istediğinizden emin misiniz?`)) return;
    try {
      await axios.put("https://json-configrator-api.root.myoctoopidev.com/User/ResetPassword", { id });
      message.success("Şifre başarıyla sıfırlandı.");
    } catch { message.error("Şifre sıfırlama başarısız."); }
  };

  const handleDeleteUser = async (id, name, surname) => {
    if (!window.confirm(`"${name} ${surname}" kullanıcısını kalıcı olarak silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete("https://json-configrator-api.root.myoctoopidev.com/User/Delete", { data: { id } });
      message.success("Kullanıcı başarıyla silindi.");
      fetchAllData();
    } catch { message.error("Kullanıcı silme başarısız."); }
  };

  const handleConfigClick = (id) => {
    localStorage.setItem('selectedUserId', id);
    navigate('/admineditableforuserjson');
  };

  const downloadConfig = (configString, customerId) => {
    try {
      const blob = new Blob([JSON.stringify(JSON.parse(configString), null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${customerId}_config.json`; a.click();
      URL.revokeObjectURL(url);
      message.success("Config dosyası indirildi!");
    } catch { message.error("Config verisi geçersiz JSON formatında."); }
  };

  const handleDelete = (record) => {
    axios.delete("https://json-configrator-api.root.myoctoopidev.com/Customer/Delete", {
      headers: { Authorization: `Bearer ${token}` }, data: { id: record.id },
    }).then(res => {
      if (res.data.success) { message.success("Başarıyla silindi"); fetchAllData(); }
      else message.error("Silme işlemi başarısız.");
    }).catch(() => message.error("API isteği başarısız."));
  };

  const handleCreateUser = async (values) => {
    try {
      const res = await fetch('https://json-configrator-api.root.myoctoopidev.com/User/Create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, role: values.role || true }),
      });
      if (!res.ok) { message.error('Kullanıcı oluşturulamadı!'); return; }
      message.success('Kullanıcı başarıyla oluşturuldu!');
      userForm.resetFields(); setIsModalVisible(false); fetchAllData();
    } catch { message.error('Bir hata oluştu!'); }
  };

  // ── Hesaplanan veriler ────────────────────────────────────
  const displayedUsers = quickSearch
    ? filteredUsers.filter(u =>
        `${u.name || ''} ${u.surname || ''} ${u.email || ''} ${u.companyName || ''}`
          .toLowerCase().includes(quickSearch.toLowerCase()))
    : filteredUsers;

  const adminCount   = displayedUsers.filter(u => !u.role).length;
  const userCount    = displayedUsers.filter(u => u.role).length;
  const totalConfigs = displayedUsers.reduce((s, u) => s + u.configCount, 0);

  const getInitials = (name, surname) => `${name?.[0] || ''}${surname?.[0] || ''}`.toUpperCase();

  // ── Genişleyen satır (config listesi) ─────────────────────
  const expandedRowRender = (record) => {
    const cols = [
      {
        title: 'Config Adı', dataIndex: 'configName', key: 'configName',
        render: (configName, cfg) => (
          <Space>
            <FileTextOutlined />
            <Text>{configName || `Config-${cfg.id.slice(0, 8)}`}</Text>
            {!configName && <Tag color="orange" style={{ fontSize: 10 }}>Auto</Tag>}
          </Space>
        )
      },
      {
        title: 'Boyut', dataIndex: 'config', key: 'configSize', width: 80,
        render: (cfg) => {
          const size = cfg ? new Blob([cfg]).size : 0;
          return <Tag color={size > 5000 ? 'red' : size > 2000 ? 'orange' : 'green'} style={{ fontSize: 10 }}>
            {size > 1024 ? `${(size / 1024).toFixed(1)}KB` : `${size}B`}
          </Tag>;
        }
      },
      {
        title: 'İşlemler', key: 'actions', width: 120,
        render: (cfg) => (
          <Space size={4}>
            <Tooltip title="İndir">
              <Button type="primary" icon={<DownloadOutlined />} size="small"
                onClick={() => downloadConfig(cfg.config, cfg.id)} disabled={!cfg.config} />
            </Tooltip>
            <Tooltip title="Düzenle">
              <Button icon={<EditOutlined />} size="small"
                onClick={() => navigate("/adminforselfupdateeditablejson", { state: { configString: cfg.config, configId: cfg.id } })}
                disabled={!cfg.config} />
            </Tooltip>
            <Tooltip title="Sil">
              <Button type="primary" danger icon={<DeleteOutlined />} size="small"
                onClick={() => handleDelete(cfg)} />
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div style={{ padding: '10px 0 6px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 10, paddingBottom: 8,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <FileTextOutlined style={{ color: '#1890ff', fontSize: 14 }} />
          <Text strong style={{ color: '#262626', fontSize: 13 }}>
            Config Listesi
          </Text>
          <Tag color="blue" style={{ borderRadius: 20, marginLeft: 2 }}>
            {record.customers.length} adet
          </Tag>
        </div>
        {record.customers.length > 0 ? (
          <Table
            columns={cols}
            dataSource={record.customers}
            pagination={false}
            size="small"
            rowKey="id"
            style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}
          />
        ) : (
          <div style={{
            textAlign: 'center', padding: '24px',
            background: '#fafafa', borderRadius: 8, color: '#bfbfbf',
          }}>
            <FileTextOutlined style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
            <Text type="secondary">Bu kullanıcıya ait config bulunamadı</Text>
          </div>
        )}
      </div>
    );
  };

  // ── Tablo kolonları ───────────────────────────────────────
  const columns = [
    {
      title: 'Kullanıcı', key: 'user', width: 190,
      render: (_, r) => (
        <Space size={8}>
          <Avatar
            style={{ backgroundColor: r.role ? '#1890ff' : '#595959', flexShrink: 0 }}
            size={28}
          >
            {getInitials(r.name, r.surname)}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {r.name} {r.surname}
            </Text>
            <Tag color={r.role ? 'blue' : 'default'} style={{ fontSize: 11, marginTop: 2 }}>
              {r.role ? 'User' : 'Admin'}
            </Tag>
          </div>
        </Space>
      )
    },
    {
      title: 'İletişim', key: 'contact', width: 200,
      render: (_, r) => (
        <div style={{ lineHeight: '20px' }}>
          <div>
            <MailOutlined style={{ color: '#bfbfbf', marginRight: 5 }} />
            <Text copyable={{ tooltips: false }} style={{ fontSize: 12 }}>{r.email}</Text>
          </div>
          {r.customerPhone && (
            <div>
              <PhoneOutlined style={{ color: '#bfbfbf', marginRight: 5 }} />
              <Text style={{ fontSize: 12 }}>{r.customerPhone}</Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Firma & Ülke', key: 'company', width: 160,
      render: (_, r) => (
        <div style={{ lineHeight: '20px' }}>
          {r.companyName && (
            <div>
              <BankOutlined style={{ color: '#bfbfbf', marginRight: 5 }} />
              <Text style={{ fontSize: 12 }}>{r.companyName}</Text>
            </div>
          )}
          {r.customerCountry && (
            <div>
              <EnvironmentOutlined style={{ color: '#bfbfbf', marginRight: 5 }} />
              <Text style={{ fontSize: 12 }}>{r.customerCountry}</Text>
            </div>
          )}
          {!r.companyName && !r.customerCountry &&
            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>}
        </div>
      )
    },
    {
      title: 'Karavan', dataIndex: 'caravanType', key: 'caravanType', width: 90,
      render: (v) => v
        ? <Tag style={{ borderRadius: 4 }}>{v}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'Config', key: 'configStatus', align: 'center', width: 70,
      render: (_, r) => (
        <Badge
          count={r.configCount}
          showZero
          style={{ backgroundColor: r.configCount > 0 ? '#1890ff' : '#d9d9d9' }}
        />
      )
    },
    {
      title: 'İşlemler', key: 'actions', width: 160,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Config Ekle">
            <Button icon={<EditOutlined />} size="small"
              onClick={() => handleConfigClick(r.id)}
              style={{ borderColor: '#1890ff', color: '#1890ff' }} />
          </Tooltip>
          {r.role ? (
            <Tooltip title="Admin Yap">
              <Button type="primary" icon={<ArrowUpOutlined />} size="small"
                onClick={() => handleRoleChange(r.id, true)} />
            </Tooltip>
          ) : (
            <Tooltip title="User Yap">
              <Button danger icon={<ArrowDownOutlined />} size="small"
                onClick={() => handleRoleChange(r.id, false)} />
            </Tooltip>
          )}
          <Tooltip title="Şifre Sıfırla">
            <Button icon={<LockOutlined />} size="small"
              onClick={() => handleResetPassword(r.id, r.name, r.surname)}
              style={{ borderColor: '#fa8c16', color: '#fa8c16' }} />
          </Tooltip>
          <Tooltip title="Kullanıcıyı Sil">
            <Button danger icon={<DeleteOutlined />} size="small"
              onClick={() => handleDeleteUser(r.id, r.name, r.surname)} />
          </Tooltip>
        </Space>
      )
    },
  ];

  // ── Render ────────────────────────────────────────────────
  return (
    <AppAdminLayout>
      <div style={{ padding: 16 }}>
        <Card
          style={{ borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none' }}
          bodyStyle={{ padding: '16px 20px' }}
        >
          {/* Başlık + Arama + Butonlar — tek satır */}
          <Row align="middle" gutter={8} style={{ marginBottom: 12 }}>
            <Col>
              <Title level={5} style={{ margin: '0 0 4px', fontWeight: 700, color: '#262626', whiteSpace: 'nowrap' }}>
                Kullanıcı Yönetimi
              </Title>
              <Space size={4}>
                <Tag color="blue"  style={{ borderRadius: 20, fontWeight: 500 }}>Toplam {displayedUsers.length}</Tag>
                <Tag color="default" style={{ borderRadius: 20 }}>Admin {adminCount}</Tag>
                <Tag color="green" style={{ borderRadius: 20 }}>User {userCount}</Tag>
                <Tag color="orange" style={{ borderRadius: 20 }}>Config {totalConfigs}</Tag>
              </Space>
            </Col>
            <Col flex="auto">
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Ad, email veya firma ara..."
                value={quickSearch}
                onChange={e => { setQuickSearch(e.target.value); setCurrentPage(1); }}
                allowClear
                size="small"
                style={{ borderRadius: 6 }}
              />
            </Col>
            <Col>
              <Space size={4}>
                <Button size="small" icon={<FilterOutlined />} onClick={() => setFilterDrawerVisible(true)}>Filtrele</Button>
                <Button size="small" icon={<ClearOutlined />} onClick={clearAllFilters}>Temizle</Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchAllData} loading={loading}>Yenile</Button>
                <Button size="small"
                  icon={expandedRows.length > 0 ? <MinusOutlined /> : <PlusOutlined />}
                  onClick={() => setExpandedRows(expandedRows.length > 0 ? [] : displayedUsers.map(u => u.id))}>
                  {expandedRows.length > 0 ? 'Kapat' : 'Tümünü Aç'}
                </Button>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                  Yeni Kullanıcı
                </Button>
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: '0 0 10px' }} />

          {/* Tablo */}
          <div ref={tableRef}>
            <Table
              columns={columns}
              dataSource={displayedUsers}
              rowKey="id"
              loading={loading}
              size="small"
              expandable={{
                expandedRowKeys: expandedRows,
                onExpand: (expanded, record) => setExpandedRows(expanded ? [record.id] : []),
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.customers?.length > 0 ? (
                    expanded
                      ? <DownOutlined onClick={e => onExpand(record, e)} style={{ cursor: 'pointer', fontSize: 11, color: '#1890ff' }} />
                      : <RightOutlined onClick={e => onExpand(record, e)} style={{ cursor: 'pointer', fontSize: 11, color: '#595959' }} />
                  ) : null,
                expandedRowRender,
                expandIconColumnIndex: 0,
              }}
              pagination={{
                total: displayedUsers.length,
                current: currentPage,
                pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '15', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kullanıcı`,
                onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
                onShowSizeChange: (_, size) => { setCurrentPage(1); setPageSize(size); },
                size: 'small',
              }}
              scroll={{ x: 'max-content' }}
            />
          </div>
        </Card>

        {/* ── Filtreler Drawer ── */}
        <Drawer
          title={<Space><FilterOutlined /> Gelişmiş Filtreler</Space>}
          placement="right"
          width={320}
          onClose={() => setFilterDrawerVisible(false)}
          visible={filterDrawerVisible}
          extra={
            <Space>
              <Button size="small" onClick={clearAllFilters}>Temizle</Button>
              <Button type="primary" size="small" onClick={() => setFilterDrawerVisible(false)}>Uygula</Button>
            </Space>
          }
        >
          <Form layout="vertical" size="small">
            <Divider orientation="left" plain style={{ fontSize: 12, margin: '0 0 10px' }}>Kullanıcı</Divider>
            {[
              { label: 'Ad',           key: 'name',            ph: 'Ada göre'           },
              { label: 'Soyad',        key: 'surname',         ph: 'Soyadına göre'      },
              { label: 'Email',        key: 'email',           ph: 'Email\'e göre'      },
              { label: 'Firma',        key: 'companyName',     ph: 'Firma adına göre'   },
              { label: 'Telefon',      key: 'customerPhone',   ph: 'Telefona göre'      },
              { label: 'Ülke',         key: 'customerCountry', ph: 'Ülkeye göre'        },
              { label: 'Karavan Tipi', key: 'caravanType',     ph: 'Karavan tipine göre'},
            ].map(f => (
              <Form.Item key={f.key} label={f.label} style={{ marginBottom: 8 }}>
                <Input
                  value={filters[f.key]}
                  onChange={e => setFilters({ ...filters, [f.key]: e.target.value })}
                  placeholder={f.ph} allowClear size="small"
                />
              </Form.Item>
            ))}
            <Form.Item label="Rol" style={{ marginBottom: 8 }}>
              <Select value={filters.role} onChange={v => setFilters({ ...filters, role: v })}
                placeholder="Tümü" allowClear style={{ width: '100%' }} size="small">
                <Option value={false}>Admin</Option>
                <Option value={true}>User</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Config Durumu" style={{ marginBottom: 8 }}>
              <Select value={filters.hasConfigs} onChange={v => setFilters({ ...filters, hasConfigs: v })}
                placeholder="Tümü" allowClear style={{ width: '100%' }} size="small">
                <Option value={true}>Config'i Var</Option>
                <Option value={false}>Config'i Yok</Option>
              </Select>
            </Form.Item>
            <Divider orientation="left" plain style={{ fontSize: 12, margin: '4px 0 10px' }}>Config</Divider>
            <Form.Item label="Config Adı / ID" style={{ marginBottom: 8 }}>
              <Input value={filters.configName}
                onChange={e => setFilters({ ...filters, configName: e.target.value })}
                placeholder="Config adı veya ID" allowClear size="small" />
            </Form.Item>
            <Form.Item label="Tam Config ID" style={{ marginBottom: 8 }}>
              <Input value={filters.configId}
                onChange={e => setFilters({ ...filters, configId: e.target.value })}
                placeholder="Tam config ID" allowClear size="small" />
            </Form.Item>
          </Form>
        </Drawer>
      </div>

      {/* ── Yeni Kullanıcı Modal ── */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Yeni Kullanıcı Oluştur</span>}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); userForm.resetFields(); }}
        footer={null}
        width={700}
        bodyStyle={{ padding: '20px 28px 8px' }}
      >
        <Form form={userForm} layout="vertical" onFinish={handleCreateUser}>
          <Row gutter={16}>
            {[
              { name: 'name',            label: 'Ad',           rules: [{ required: true, message: 'Zorunlu alan' }] },
              { name: 'surname',         label: 'Soyad',        rules: [{ required: true, message: 'Zorunlu alan' }] },
              { name: 'email',           label: 'Email',        rules: [{ required: true }, { type: 'email', message: 'Geçerli email girin' }] },
              { name: 'password',        label: 'Şifre',        rules: [{ required: true, message: 'Zorunlu alan' }], password: true },
              { name: 'companyName',     label: 'Firma Adı',    rules: [] },
              { name: 'caravanType',     label: 'Karavan Tipi', rules: [] },
              { name: 'customerPhone',   label: 'Telefon',      rules: [] },
              { name: 'customerCountry', label: 'Ülke',         rules: [] },
            ].map(f => (
              <Col span={12} key={f.name}>
                <Form.Item name={f.name} label={f.label} rules={f.rules}>
                  {f.password
                    ? <Input.Password placeholder={f.label} style={{ borderRadius: 6 }} />
                    : <Input placeholder={f.label} style={{ borderRadius: 6 }} />
                  }
                </Form.Item>
              </Col>
            ))}
            <Col span={24}>
              <Form.Item name="description" label="Açıklama">
                <Input.TextArea placeholder="Açıklama (isteğe bağlı)" rows={2} style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ margin: '0 0 14px' }} />
          <Form.Item style={{ textAlign: 'right', marginBottom: 16 }}>
            <Button onClick={() => { setIsModalVisible(false); userForm.resetFields(); }} style={{ marginRight: 8 }}>
              İptal
            </Button>
            <Button type="primary" htmlType="submit" style={{ fontWeight: 600, minWidth: 90 }}>
              Oluştur
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </AppAdminLayout>
  );
};

export default UnifiedUserCustomerManagement;
